<script setup lang="ts">
import {
	useRoute,
	useRouter
} from "vue-router";
import {
	computed,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	watch
} from "vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import FileBlock from "@/components/main/project-block-page/file-block/FileBlock.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import ClientAddUnionDropdown from "@/components/main/client-block-page/dropdowns/ClientAddUnionDropdown.vue";
import ClientAddWorkerDropdown from "@/components/main/client-block-page/dropdowns/ClientAddWorkerDropdown.vue";
import ClientAddRoleDropdown from "@/components/main/client-block-page/dropdowns/ClientAddRoleDropdown.vue";
import DeleteClientConfirmDropdown from "@/components/main/client-block-page/dropdowns/DeleteClientConfirmDropdown.vue";
import ClientAddCounterpartyDropdown from "@/components/main/client-block-page/dropdowns/ClientAddCounterpartyDropdown.vue";
import DetachCounterpartyConfirmDropdown from "@/components/main/client-block-page/dropdowns/DetachCounterpartyConfirmDropdown.vue";
import CounterpartyInfoModal from "@/components/main/client-block-page/modals/CounterpartyInfoModal.vue";
import ClientInfoModal from "@/components/main/client-block-page/modals/ClientInfoModal.vue";
import HoldingExpandModal from "@/components/main/client-block-page/modals/HoldingExpandModal.vue";
import CounterpartiesExpandModal from "@/components/main/client-block-page/modals/CounterpartiesExpandModal.vue";
import { useOverflowDetection } from "@/composables/useOverflowDetection";
import AppScrollTopButton from "@/components/ui-kit/button/AppScrollTopButton.vue";
import { useScrollTopButton } from "@/composables/useScrollTopButton";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import FileInput from "@/components/file/FileInput.vue";
import InlineAddressEditor from "@/components/common/address-selector/InlineAddressEditor.vue";
import { AddressInputContainer } from "@/models/interfaces/entity-settings";
import { chainToRequestContainer, RequestAddressContainer } from "@/composables/useAddressRequest";
import ParametersSection from "@/components/main/shared/ParametersSection.vue";
import { V2ParameterAttachmentType } from "@/models/etc/parameter-types";
import { clientPageStore } from "@/store/client-page";
import { auxStore } from "@/store/auxillary";
import { favouritesStore } from "@/store/favourites";
import { TagEntityType } from "@/models/etc/types";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import moment from "moment/moment";
import { workerStore } from "@/store/worker";
import { buildAbsoluteClientUrl } from "@/utils/urlHelpers";
import { copyTextToClipboard } from "@/utils/clipboard";
import { formatMoney } from "@/utils/format";
import { i18n } from "@/i18n";

const clientStore = clientPageStore()
const favourStore = favouritesStore()
const workerStorage = workerStore();
const route = useRoute()
const router = useRouter()

const isClientLoading = computed(() => clientStore.clientLoading)
const isFavouritesLoading = computed(() => favourStore.isFavouritesLoading)
const clientId = computed(() => parseInt(route.query.id as string))
const client = computed(() => clientStore.openedClient)

const $clientPage = ref<HTMLElement | null>(null)
const { isScrollTopEnabled, scrollToTop } = useScrollTopButton(
	$clientPage,
	'.client-page__header',
	() => client.value?.Id
)

// Меню добавления параметра открывается из "+" в шапке блока «Параметры»
const parametersAddOpen = ref(false)
const parametersAddAnchor = ref<HTMLElement | null>(null)
const getTotalRevenue = computed(() => formatMoney(
	client.value
		? client.value.Counterparties.reduce((acc, cur) => acc + cur.Revenue, 0)
		: 0
))

const isAdmin = workerStorage.workerProfile?.IsAdmin;

const hasAnyRestrictedAccess = computed(() => {
  const c = clientStore.client;
  if (!c) return false;

  if (isAdmin) return false;

  return (
    c.AccessLevel === 0 ||
    c.NameAccess === 0 ||
    c.AttachedWorkersAccess === 0 ||
    c.AddressAccess === 0 ||
    c.UnionAccess === 0 ||
    c.RuleAccess === 0 ||
    c.ParameterAccess === 0 ||
    c.FileAccess === 0 ||
	c.CounterpartiesAccess === 0 ||
	c.JobAccess === 0 ||
	c.PersonAccess === 0 ||
	c.JobPreviousAccess === 0 ||
    c.DeleteAccess === 0
  );
});

const getTotalDebt = computed(() => formatMoney(
	client.value
		? client.value.Counterparties.reduce((acc, cur) => acc + cur.Debt, 0)
		: 0
))

// --- Раскрытие блоков на весь экран (модалки) ---
// Стрелка «на весь экран» показывается только когда тело блока переполнено
// и в нём появился скролл (см. useOverflowDetection).
const $holdingBody = ref<HTMLElement | null>(null)
const $cpBody = ref<HTMLElement | null>(null)
const $holdingModal = ref<InstanceType<typeof HoldingExpandModal> | null>(null)
const $parametersSection = ref<InstanceType<typeof ParametersSection> | null>(null)
const $cpModal = ref<InstanceType<typeof CounterpartiesExpandModal> | null>(null)

const { isOverflowing: isHoldingOverflowing } = useOverflowDetection(
	$holdingBody,
	() => client.value?.UnionClients?.length ?? 0,
)
const { isOverflowing: isCpOverflowing } = useOverflowDetection(
	$cpBody,
	() => client.value?.Counterparties?.length ?? 0,
)

const fetchClient = async () => {
	await clientStore.getClient(clientId.value)
}

const toggleFavourite = async () => {
	try {
		if (!isFavouritesLoading.value) {
			if (!client.value?.Favorite) {
				await favourStore.addFavourites(clientId.value, 0)
			} else {
				await favourStore.removeFavourites(clientId.value, 0)
			}
			clientStore.updateCurrentClientFavouriteState(!client.value?.Favorite)
		}
	} catch (error) {
		console.error(error)
	}
}

const $shareTooltip = ref<typeof AppTooltip | null>(null)

const SHARE_TOOLTIP_KEY = 'ui_share'
const SHARE_COPIED_KEY = 'link_copied_to_clipboard'
const SHARE_COPIED_DELAY = 3000

const shareTooltipKey = ref(SHARE_TOOLTIP_KEY)
const shareTooltipText = computed(() => String(i18n.global.t(shareTooltipKey.value)))

let shareTooltipTimer: ReturnType<typeof setTimeout> | null = null

const onCopyClientRoute = async () => {
	if (Number.isNaN(clientId.value)) {
		return
	}

	const copied = await copyTextToClipboard(
		buildAbsoluteClientUrl(`main?page=client&id=${clientId.value}`)
	)
	if (!copied) {
		return
	}

	if (shareTooltipTimer) {
		clearTimeout(shareTooltipTimer)
	}
	shareTooltipKey.value = SHARE_COPIED_KEY
	$shareTooltip.value?.showByClick()
	shareTooltipTimer = setTimeout(() => {
		shareTooltipKey.value = SHARE_TOOLTIP_KEY
		shareTooltipTimer = null
	}, SHARE_COPIED_DELAY)
}

onBeforeUnmount(() => {
	if (shareTooltipTimer) {
		clearTimeout(shareTooltipTimer)
	}
})

const getCreatedDate = (date: string) => moment(date).format("DD.MM.YYYY");

const $clientInfoModal = ref<InstanceType<typeof ClientInfoModal> | null>(null)

const $clientNameInput = ref<InstanceType<typeof FileInput> | null>(null)
const isClientNameEdited = ref(false)
const editedClientName = ref("")

const editClientName = () => {
	editedClientName.value = client.value?.Name ?? ""
	isClientNameEdited.value = true
	nextTick(() => $clientNameInput.value?.focus())
}

const saveClientName = async () => {
	isClientNameEdited.value = false
	const trimmed = editedClientName.value.trim()
	if (!trimmed || trimmed === client.value?.Name) return
	try {
		await clientStore.renameCurrentClient(trimmed)
	} catch (error) {
		console.error(error)
	}
}

const clientAddressContainer = computed<RequestAddressContainer | null>(() =>
	chainToRequestContainer({
		Components: client.value?.AddressComponents ?? [],
		ActiveLevel: 0,
	})
)

const onClientAddressSave = async (chain: AddressInputContainer) => {
	try {
		await clientStore.updateCurrentClientAddress(chain)
	} catch (error) {
		console.error(error)
	}
}

const openClientInfoModal = () => {
	$clientInfoModal.value?.onOpen(clientId.value, client.value?.Tags ?? [])
}

const $addTag = ref<typeof AppResizableDropdown | null>(null)

const clientTagsModel = computed<TagWithFrequency[]>(
	() => (client.value?.Tags ?? []) as unknown as TagWithFrequency[]
)

const onSelectorChange = async (nextTags: TagWithFrequency[]) => {
	const prevIds = new Set((client.value?.Tags ?? []).map(t => t.Id))
	const nextIds = new Set(nextTags.map(t => t.Id))

	const added = nextTags.filter(t => !prevIds.has(t.Id))
	const removedIds = [...prevIds].filter(id => !nextIds.has(id))

	try {
		for (const tag of added) await clientStore.attachTagToClient({ Name: tag.Name })
		for (const id of removedIds) await clientStore.detachTagFromClient(id)
	} catch (e) {
		console.error(e)
	}
}

const onCreateTag = async (name: string) => {
	try {
		await clientStore.attachTagToClient({ Name: name })
	} catch (e) {
		console.error(e)
	}
}

const onDetachTag = async (tagId: number) => {
	try {
		await clientStore.detachTagFromClient(tagId)
	} catch (e) {
		console.error(e)
	}
}

const $addUnionClient = ref<typeof AppResizableDropdown | null>(null)

const closeAddUnionDropdown = () => {
	$addUnionClient.value?.close()
}

const onDetachUnionClient = async (id: number) => {
	try {
		await clientStore.detachClientFromUnion(id)
	} catch (e) {
		console.error(e)
	}
}

const $addCounterparty = ref<typeof AppResizableDropdown | null>(null)
const $cpInfoModal = ref<InstanceType<typeof CounterpartyInfoModal> | null>(null)
const $cpDetach = ref<Record<number, any>>({})

const isCpInfoOpen = ref(false)

const openCpInfo = (counterpartyId: number) => {
	isCpInfoOpen.value = true
	$cpInfoModal.value?.onOpen(counterpartyId, canEditCounterparties.value)
}

const canEditCounterparties = computed(() =>
	clientStore.client?.CounterpartiesAccess == 2
)
const canViewCounterparties = computed(() =>
	clientStore.client?.CounterpartiesAccess !== 0
)

const counterpartyDropdownMode = ref<'search' | 'create'>('search')

const closeAddCounterpartyDropdown = () => {
	$addCounterparty.value?.close()
}

const onDetachCounterparty = async (id: number) => {
	try {
		await clientStore.detachCounterparty(id)
	} catch (e) {
		console.error(e)
	} finally {
		$cpDetach.value[id]?.close()
	}
}

const $addWorker = ref<typeof AppResizableDropdown | null>(null)

const closeAddWorkerDropdown = () => {
	$addWorker.value?.close()
}

const onDetachWorker = async (id: number) => {
	try {
		await clientStore.detachWorkerFromClient(id)
	} catch (e) {
		console.error(e)
	}
}

const $addRoles = ref<Record<number, any>>({})

const closeAddRoleDropdown = (awId: number) => {
	$addRoles.value[awId]?.close()
}

const onDetachRole = async (workerId: number, roleId: number) => {
	try {
		await clientStore.detachRoleFromWorker(workerId, roleId)
	} catch (e) {
		console.error(e)
	}
}

const $deleteClient = ref<InstanceType<typeof AppResizableDropdown> | null>(null)

const onConfirmDelete = async () => {
	if (isClientLoading.value) return
	try {
		await clientStore.deleteClient()
		await router.push({
			path: '/',
			query: {
				page: 'chat',
				mode: 'list',
			},
		})
	} catch (error) {
		console.error(error)
		$deleteClient.value?.close()
	}
}

watch(
	() => route.query.id,
	async (newVal, oldVal) => {
		if (newVal !== oldVal) {
			await fetchClient()
		}
	}
)

onMounted(async () => {
	// Прогрев глобального кеша структуры адреса
	auxStore().getAddressContainerStructure()
	await fetchClient()
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))
</script>

<template>
	<div class="client-page" ref="$clientPage">
		<app-loader v-if="isClientLoading" :style="loaderStyle"/>
		<app-scroll-top-button
			class="client-page__scroll-top"
			:enabled="isScrollTopEnabled"
			@on-click="scrollToTop"
		/>
		<div class="client-page__header">
			<div class="client-page__header-person">
				<div class="client-page__header-person-name">
					{{ client?.Creator.Name || $t('user_label') }}
				</div>
				<div class="client-page__header-person-date">
					{{ client ? getCreatedDate(client.CreateTime) : '' }}
				</div>
				<app-resizable-dropdown
					v-if="client?.CanBeDeleted && clientStore.client?.DeleteAccess!==0"
					ref="$deleteClient"
					position="right"
					centered-on-mobile
					:close-by-click-outside="true"
					name="$deleteClient"
				>
					<template #activator>
						<app-tooltip :text="$t('remove')">
							<template #activator>
								<app-icon
									class="client-page__header-person-icon"
									name="delete-outline"
									width="18px"
									color="#979797"
									hover-color="#BA4C4C"
									cursor="pointer"
								/>
							</template>
						</app-tooltip>
					</template>
					<delete-client-confirm-dropdown
						:loading="isClientLoading"
						@cancel="$deleteClient?.close()"
						@confirm="onConfirmDelete"
					/>
				</app-resizable-dropdown>
			</div>
			<div class="client-page__header-options">
				<app-tooltip :text="$t(client?.Favorite ? 'favourite_remove' : 'add_to_favorites')">
					<template #activator>
						<app-icon
							class="client-page__header-options-icon"
							name="favourite"
							width="18px"
							:color="client?.Favorite ? '#D05A4F' : '#979797'"
							:hover-color="client?.Favorite ? '#979797' : '#D05A4F'"
							cursor="pointer"
							@click="toggleFavourite"
						/>
					</template>
				</app-tooltip>
				<app-tooltip
					ref="$shareTooltip"
					:text="shareTooltipText"
				>
					<template #activator>
						<app-icon
							class="client-page__header-options-icon"
							:style="{ marginTop: '1px' }"
							name="share"
							width="18px"
							color="#979797"
							hover-color="#499259"
							cursor="pointer"
							@click="onCopyClientRoute"
						/>
					</template>
				</app-tooltip>
				<app-resizable-dropdown
					v-if="clientStore.client?.AccessLevel!==0"
					ref="$addTag"
					position="right"
					:close-by-click-outside="true"
					:max-width="640"
					:max-height="460"
					:mobile-title="$t('choose_tags')"
					name="$addTagClientPage"
				>
					<template #activator>
						<app-tooltip :text="$t('file_add_tag')">
							<template #activator>
								<app-icon
									class="client-page__header-options-icon"
									name="hash"
									width="18px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
								/>
							</template>
						</app-tooltip>
					</template>
					<app-tag-selector
						:model-value="clientTagsModel"
						:entity-type="TagEntityType.Client"
						mode="select"
						allow-create
						@update:model-value="onSelectorChange"
						@create-tag="onCreateTag"
						@close="$addTag?.close()"
					/>
				</app-resizable-dropdown>
				<app-icon
					v-else
					class="client-page__header-options-icon"
					name="hash"
					width="18px"
					color="#D2D2D2"
				/>
				<div
					v-if="client?.Tags?.length"
					class="client-page__header-options-tags"
				>
					<div
						v-for="tag in client.Tags"
						:key="tag.Id"
						class="client-page__header-options-tags-item"
					>
						<div class="client-page__header-options-tags-item-name">
							{{ tag.Name.toLowerCase() }}
						</div>
						<app-tooltip :text="$t('ui_tag_detach')" v-if="clientStore.client?.AccessLevel!==0">
							<template #activator>
								<app-icon
									class="client-page__direction-aw-left-icon"
									:style="{ marginTop: '-2px' }"
									name="close-bold"
									width="12px"
									color="#D2D2D2"
									hover-color="#BA4C4C"
									cursor="pointer"
									@click="onDetachTag(tag.Id)"
								/>
							</template>
						</app-tooltip>
					</div>
				</div>
			</div>
		</div>
		<div class="client-page__header-permition" v-if="hasAnyRestrictedAccess">
			<app-icon
				class="client-page__header-permition-icon"
				name="invalid"
				width="16px"
				color="#979797"
			/>
			<div class="client-page__header-permition-label">
				{{ $t('client_limited_access') }}
			</div>
		</div>
		<div class="client-page__divider" />
		<div class="client-page__client">
			<div class="client-page__client-icon-block">
				<app-tooltip :text="$t('info')" v-if="clientStore.client?.AccessLevel==1">
					<template #activator>
						<app-icon
							class="client-page__client-icon"
							:style="{ marginTop: '-2px' }"
							name="info-circle-outline"
							width="18px"
							color="#979797"
							hover-color="#499259"
							cursor="pointer"
							@click="openClientInfoModal"
						/>
					</template>
				</app-tooltip>
				<app-icon
					v-else
					class="client-page__client-icon"
					:style="{ marginTop: '-2px' }"
					name="info-circle-outline"
					width="18px"
					color="#D2D2D2"
				/>
			</div>
			<div class="client-page__client-title">
				{{ $t('client_label') }}{{clientStore.client?.NameAccess!==0 ? ':' : ''}}
				<file-input
					v-if="isClientNameEdited"
					ref="$clientNameInput"
					v-model="editedClientName"
					@blur="saveClientName"
				/>
				<template v-else>
					<div class="client-page__client-name" v-if="clientStore.client?.NameAccess!==0">
						{{ client?.Name || '' }}
					</div>
					<app-tooltip class="client-page__client-edit" :text="$t('edit_label')" v-if="clientStore.client?.NameAccess==2">
						<template #activator>
							<app-icon
								class="client-page__client-icon"
								:style="{ marginTop: '-2px' }"
								name="edit-pencil"
								width="18px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
								@click="editClientName"
							/>
						</template>
					</app-tooltip>
				</template>
			</div>
		</div>
		<div class="client-page__divider" />
		<div class="client-page__address">
			<div class="client-page__client-icon-block">
				<app-icon
					class="client-page__address-icon"
					:style="{ marginTop: '-2px' }"
					name="address"
					width="18px"
					:color="clientStore.client?.AddressAccess==2 ? '#979797' : '#D2D2D2'"
				/>
			</div>
			<div class="client-page__address-title">
				{{ $t('address_upcase') }}{{clientStore.client?.AddressAccess!==0 ? ':' : ''}}
				<inline-address-editor
					v-if="clientStore.client?.AddressAccess !== 0"
					:id="'client-address'"
					:key="client?.Id"
					:model-value="clientAddressContainer"
					:editable="clientStore.client?.AddressAccess === 2"
					:show-empty-placeholder="false"
					@save="onClientAddressSave"
				/>
			</div>

		</div>
		<div class="client-page__divider" />
		<div
			v-if="canViewCounterparties"
			class="client-page__direction"
		>
			<div class="client-page__direction-header">
				<div class="client-page__direction-header-left">
					<app-resizable-dropdown
						v-if="canEditCounterparties"
						position="right"
						resizable
						:initial-width="600"
						:initial-height="630"
						:min-width="440"
						:max-width="900"
						:min-height="480"
						:max-height="900"
						:close-by-click-outside="counterpartyDropdownMode === 'search' && !isCpInfoOpen"
						:mobile-title="$t('choose_counterparties')"
						ref="$addCounterparty"
						name="$addCounterparty"
						@close="counterpartyDropdownMode = 'search'"
					>
						<template #activator>
							<app-tooltip :text="$t('ui_add')">
								<template #activator>
									<app-icon
										class="client-page__direction-header-icon"
										name="plus"
										width="15px"
										color="#979797"
										hover-color="#499259"
										cursor="pointer"
									/>
								</template>
							</app-tooltip>
						</template>
						<client-add-counterparty-dropdown
							v-if="client"
							:client-id="client.Id"
							:added-counterparties="client.Counterparties ?? []"
							show-close-icon
							@mode-change="counterpartyDropdownMode = $event"
							@open-info="openCpInfo"
							@close="closeAddCounterpartyDropdown"
						/>
					</app-resizable-dropdown>
					<app-icon
						v-else
						class="client-page__direction-header-icon"
						name="plus"
						width="15px"
						color="#D2D2D2"
					/>
					<div class="client-page__direction-header-title">
						{{ $t('client_counterparties_title') }}
					</div>
					<app-tooltip
						v-if="isCpOverflowing"
						:text="$t('ui_fullscreen')"
					>
						<template #activator>
							<app-icon
								class="client-page__direction-header-icon"
								name="arrow-expand"
								width="18px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
								@click="$cpModal?.onOpen()"
							/>
						</template>
					</app-tooltip>
				</div>
				<div
					v-if="client?.Counterparties.length"
					class="client-page__direction-header-right"
				>
					[{{ $t('client_revenue_label') }} <span :style="{margin: '0 1px'}">/</span> {{ $t('client_debt_label') }}: {{ getTotalRevenue }}
					<span :style="{margin: '0 1px'}">/</span>
					<span class="client-page__direction-header-right-debt">{{ getTotalDebt }}</span>]
				</div>
			</div>
			<div
				v-if="client?.Counterparties && client?.Counterparties.length"
				ref="$cpBody"
				:class="{
					'client-page__direction-body': true,
					'client-page__direction-body--cp': true
				}"
			>
				<div
					v-for="(cp, index) in client.Counterparties"
					:key="cp.Id"
					class="client-page__direction-cp"
				>
					<div class="client-page__direction-cp-left">
						<div class="client-page__direction-cp-left-count">{{ index + 1 }}.</div>
						<div class="client-page__direction-cp-left-name">
							{{ cp.Name }}<span :style="{whiteSpace: 'nowrap'}">&nbsp;<template v-if="cp.Instance1C">[{{ cp.Instance1C }}]&nbsp;</template><app-tooltip
									v-if="canViewCounterparties"
									:text="$t('info')"
								>
									<template #activator>
										<app-icon
											class="client-page__direction-cp-left-icon"
											:style="{ marginTop: '-2px' }"
											name="info-circle-outline"
											width="14px"
											color="#979797"
											hover-color="#499259"
											cursor="pointer"
											@click="openCpInfo(cp.Id)"
										/>
									</template>
								</app-tooltip>
								<app-resizable-dropdown
									v-if="canEditCounterparties"
									position="right"
									:close-by-click-outside="true"
									:ref="(el: any) => { if (el) $cpDetach[cp.Id] = el }"
								>
									<template #activator>
										<app-tooltip :text="$t('remove')">
											<template #activator>
												<app-icon
													class="client-page__direction-cp-left-icon"
													:style="{ marginTop: '-2px' }"
													name="close-bold"
													width="12px"
													color="#D2D2D2"
													hover-color="#BA4C4C"
													cursor="pointer"
												/>
											</template>
										</app-tooltip>
									</template>
									<detach-counterparty-confirm-dropdown
										@cancel="$cpDetach[cp.Id]?.close()"
										@confirm="onDetachCounterparty(cp.Id)"
									/>
								</app-resizable-dropdown>
							</span>
						</div>
					</div>
					<div class="client-page__direction-cp-right">
						{{ formatMoney(cp.Revenue) }}<span :style="{margin: '0 1px'}">/</span>
						<span class="client-page__direction-header-right-debt">
							{{ formatMoney(cp.Debt) }}
						</span>
					</div>
				</div>
			</div>
		</div>
		<div
			v-if="canViewCounterparties"
			class="client-page__divider"
		/>
		<div class="client-page__direction">
			<div class="client-page__direction-header">
				<div class="client-page__direction-header-left">
					<app-resizable-dropdown
						v-if="clientStore.client?.UnionAccess==2"
						position="right"
						resizable
						:initial-width="400"
						:initial-height="450"
						:min-width="400"
						:max-width="800"
						:min-height="450"
						:max-height="900"
						:mobile-title="$t('choose_holding')"
						ref="$addUnionClient"
						name="$addUnionClient"
					>
						<template #activator>
							<app-tooltip :text="$t('ui_add')">
								<template #activator>
									<app-icon
										class="client-page__direction-header-icon"
										name="plus"
										width="15px"
										color="#979797"
										hover-color="#499259"
										cursor="pointer"
									/>
								</template>
							</app-tooltip>
						</template>
						<client-add-union-dropdown
							v-if="client"
							:client-id="client.Id"
							:added-clients="client.UnionClients"
							show-close-icon
							@close="closeAddUnionDropdown"
						/>
					</app-resizable-dropdown>
					<app-icon
						v-else-if="clientStore.client?.UnionAccess!==2"
						class="client-page__direction-header-icon"
						name="plus"
						width="15px"
						color="#D2D2D2"
					/>
					<div class="client-page__direction-header-title">
						{{ $t('holding') }}
					</div>
					<app-tooltip
						v-if="isHoldingOverflowing && clientStore.client?.UnionAccess!==0"
						:text="$t('ui_fullscreen')"
					>
						<template #activator>
							<app-icon
								class="client-page__direction-header-icon"
								name="arrow-expand"
								width="18px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
								@click="$holdingModal?.onOpen()"
							/>
						</template>
					</app-tooltip>
				</div>
				<div
					v-if="client?.UnionClients.length"
					class="client-page__direction-header-right"
				>
					[{{ $t('client_revenue_label') }} <span :style="{margin: '0 1px'}">/</span> {{ $t('client_debt_label') }}: {{ formatMoney(client?.UnionRevenue) }}
					<span :style="{margin: '0 1px'}">/</span>
					<span class="client-page__direction-header-right-debt">{{ formatMoney(client?.UnionDebt) }}</span>]
				</div>
			</div>
			<div
				v-if="client?.UnionClients && client?.UnionClients.length && clientStore.client?.UnionAccess!==0"
				ref="$holdingBody"
				:class="{
					'client-page__direction-body': true,
					'client-page__direction-body--uc': true
				}"
			>
				<div
					v-for="(uc, index) in client.UnionClients"
					:key="uc.Id"
					class="client-page__direction-uc"
				>
					<div class="client-page__direction-uc-left">
						<div class="client-page__direction-uc-left-count">{{ index + 1 }}.</div>
						<div class="client-page__direction-uc-left-name">
							<router-link
								class="client-page__direction-uc-left-name-str"
								:to="{ query: { page: 'client', id: uc.Id } }"
							>
								{{ uc.Name }}
							</router-link>
							<span class="client-page__direction-uc-left-address">
								({{ uc.Address }})
								<app-tooltip :text="$t('remove')" v-if="clientStore.client?.UnionAccess==2">
								<template #activator>
									<app-icon
										class="client-page__direction-uc-left-icon"
										:style="{ marginTop: '-2px' }"
										name="close-bold"
										width="12px"
										color="#D2D2D2"
										hover-color="#BA4C4C"
										cursor="pointer"
										@click="onDetachUnionClient(uc.Id)"
									/>
								</template>
							</app-tooltip>
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="client-page__divider" />
		<div class="client-page__direction">
			<div class="client-page__direction-header">
				<div class="client-page__direction-header-left">
					<app-resizable-dropdown
						v-if="clientStore.client?.AttachedWorkersAccess==2"
						position="right"
						resizable
						:initial-width="400"
						:initial-height="450"
						:min-width="400"
						:max-width="800"
						:min-height="450"
						:max-height="900"
						:mobile-title="$t('choose_workers')"
						ref="$addWorker"
						name="$addWorker"
					>
						<template #activator>
							<app-tooltip :text="$t('ui_add')">
								<template #activator>
									<app-icon
										class="client-page__direction-header-icon"
										name="plus"
										width="15px"
										color="#979797"
										hover-color="#499259"
										cursor="pointer"
									/>
								</template>
							</app-tooltip>
						</template>
						<client-add-worker-dropdown
							v-if="client"
							:client-id="client.Id"
							:added-workers="client.AttachedWorkers"
							show-close-icon
							@close="closeAddWorkerDropdown"
						/>
					</app-resizable-dropdown>
					<app-icon
						v-else
						class="client-page__direction-header-icon"
						name="plus"
						width="15px"
						color="#D2D2D2"
					/>
					<div class="client-page__direction-header-title">
						{{ $t('client_workers') }}
					</div>
				</div>
			</div>
			<div
				v-if="client?.AttachedWorkers && client?.AttachedWorkers.length && clientStore.client?.AttachedWorkersAccess!==0"
				:class="{
					'client-page__direction-body': true,
					'client-page__direction-body--aw': true
				}"
			>
				<div
					v-for="(aw, index) in client.AttachedWorkers"
					:key="aw.Id"
					class="client-page__direction-aw"
				>
					<div class="client-page__direction-aw-left">
						<div class="client-page__direction-aw-left-count">{{ index + 1 }}.</div>
						<div :class="{
							'client-page__direction-aw-left-name': true,
							'client-page__direction-aw-left-name--removed': aw.Removed,
						}">
							{{ aw.Name }}
							<app-tooltip :text="$t('remove')" v-if="clientStore.client?.AttachedWorkersAccess==2">
								<template #activator>
									<app-icon
										class="client-page__direction-aw-left-icon"
										:style="{ marginTop: '-2px' }"
										name="close-bold"
										width="12px"
										color="#D2D2D2"
										hover-color="#BA4C4C"
										cursor="pointer"
										@click="onDetachWorker(aw.Id)"
									/>
								</template>
							</app-tooltip>
							<app-resizable-dropdown
								v-if="clientStore.client?.AttachedWorkersAccess==2"
								position="right"
								:initial-width="400"
								:initial-height="450"
								:min-width="400"
								:max-width="800"
								:min-height="450"
								:max-height="900"
								:mobile-title="$t('choose_roles')"
								:ref="(el: any) => { if (el) $addRoles[aw.Id] = el }"
							>
								<template #activator>
									<app-tooltip :text="$t('client_add_role')">
										<template #activator>
											<app-icon
												class="client-page__direction-aw-left-icon"
												:style="{ marginTop: '-2px' }"
												name="hash"
												width="18px"
												color="#979797"
												hover-color="#499259"
												cursor="pointer"
											/>
										</template>
									</app-tooltip>
								</template>
								<client-add-role-dropdown
									v-if="client"
									:client-id="client.Id"
									:worker-id="aw.Id"
									:attached-roles="aw.Roles ?? []"
									show-close-icon
									@close="closeAddRoleDropdown(aw.Id)"
								/>
							</app-resizable-dropdown>
							<app-icon
								v-else
								class="client-page__direction-aw-left-icon"
								:style="{ marginTop: '-2px' }"
								name="hash"
								width="18px"
								color="#D2D2D2"
							/>
							<span
								v-if="aw.Roles?.length"
								class="client-page__direction-aw-left-roles"
							>
								<span
									v-for="role in aw.Roles"
									:key="role.Id"
									class="client-page__direction-aw-left-roles-item"
								>
									<span class="client-page__direction-aw-left-roles-item-name">
										{{ role.Name }}
									</span>
									<app-tooltip :text="$t('remove')" v-if="clientStore.client?.AttachedWorkersAccess==2">
										<template #activator>
											<app-icon
												class="client-page__direction-aw-left-icon"
												:style="{ marginTop: '-2px' }"
												name="close-bold"
												width="12px"
												color="#D2D2D2"
												hover-color="#BA4C4C"
												cursor="pointer"
												@click="onDetachRole(aw.Id, role.Id)"
											/>
										</template>
									</app-tooltip>
								</span>
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="client-page__divider" />
		<div
			v-if="clientStore.client?.ParameterAccess==1 || clientStore.client?.ParameterAccess==2"
			class="client-page__direction"
		>
			<div class="client-page__direction-header">
				<div class="client-page__direction-header-left">
					<app-tooltip :text="$t('ui_add')" v-if="clientStore.client?.ParameterAccess==2">
						<template #activator>
							<span
								ref="parametersAddAnchor"
								class="client-page__direction-header-add-anchor"
								@click="parametersAddOpen = true"
							>
								<app-icon
									class="client-page__direction-header-icon"
									name="plus"
									width="15px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
								/>
							</span>
						</template>
					</app-tooltip>
					<app-icon
						v-else
						class="client-page__direction-header-icon"
						name="plus"
						width="15px"
						color="#D2D2D2"
					/>
					<div class="client-page__direction-header-title">
						{{ $t('parameters_label') }}
					</div>
					<app-tooltip :text="$t('refresh_label')">
						<template #activator>
							<app-icon
								class="client-page__direction-header-icon"
								name="refresh"
								width="15px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
								@click="$parametersSection?.refresh()"
							/>
						</template>
					</app-tooltip>
				</div>
			</div>
			<div v-if="client" class="client-page__direction-body">
				<parameters-section
					ref="$parametersSection"
					:entity-id="clientId"
					:attachment-type="V2ParameterAttachmentType.Client"
					:parameter-access="clientStore.client?.ParameterAccess"
					:add-menu-target="parametersAddAnchor"
					v-model:add-menu-open="parametersAddOpen"
					@changed="fetchClient"
				/>
			</div>
		</div>
		<div class="client-page__divider" />
		<div class="client-page__direction">
			<file-block
				v-if="clientStore.client?.FileAccess!==0"
				:id="clientId"
				:root-name="client?.Name"
				type="client"
				:rule-access="clientStore.client?.FileAccess"
			/>
		</div>
		<client-info-modal
			ref="$clientInfoModal"
			:title="$t('client_information_and_access')"
		/>
		<holding-expand-modal ref="$holdingModal" />
		<counterparties-expand-modal ref="$cpModal" />
		<counterparty-info-modal
			ref="$cpInfoModal"
			@close="isCpInfoOpen = false"
		/>
	</div>
	<div class="client-page" v-if="clientStore.client?.FileAccess==0" style="justify-content: center; margin-top: 8px; max-height: 378px;">
		<div class="client-page__direction">
			<div>
				<div class="client-page__direction-permition">
					<app-icon
						class="client-page__direction-permition-icon"
						name="invalid"
						width="24px"
						color="#979797"
					/>
					<div class="client-page__direction-permition-label">
						{{ $t('client_files_no_access') }}
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
@import "@/styles/color.scss";

.client-page {
	display: flex;
	flex-direction: column;
	position: relative;
	max-height: 100%;
	height: 100%;
	width: 100%;
	padding: 12px;
	border-radius: 8px;
	background: #fff;
	overflow: auto;

	&:hover :deep(.app-scroll-top--enabled) {
		opacity: 1;
		visibility: visible;
		pointer-events: auto;
	}

	:deep(.app-scroll-top) {
		margin-right: 0;
	}

	@media (min-width: 769px) {
		:deep(.client-page__scroll-top) {
			top: calc(100% - 38px);
			bottom: auto;
		}
	}

	&__header {
		display: flow-root;

		&-permition {
			display: flex;
			flex-direction: row;
			gap: 4px;
			margin-top: 2px;

			&-label {
				color: $color-text-secondary;
				font-size: 14px;
				font-style: normal;
				font-weight: 400;
				line-height: 18px;
			}
		}

		&-options {
			display: block;
			line-height: 20px;

			> * {
				vertical-align: middle;
			}

			> * + * {
				margin-left: 4px;
			}

			&-tags {
				display: inline;

				&-item {
					display: inline-flex;
					align-items: center;
					vertical-align: middle;
					max-width: 100%;
					margin-right: 2px;

					&-name {
						color: #979797;
						margin-right: 2px;
						min-width: 0;
						overflow-wrap: anywhere;
					}
				}
			}
		}

		&-person {
			display: flex;
			align-items: center;
			gap: 4px;
			float: right;
			height: 20px;
			margin-left: 8px;
			max-width: 100%;
			white-space: nowrap;

			&-name,
			&-date {
				font-size: 14px;
				line-height: 18px;
				color: #4D4D4D;
			}

			&-name {
				padding-right: 4px;
				border-right: 1px solid #F2F2F2;
				min-width: 0;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			&-date {
				flex-shrink: 0;
			}

			&-icon {
				margin-top: -3px;
			}
		}
	}

	&__address &__client-icon-block {
		padding-top: 3px;
	}

	&__client {
		display: flex;
		gap: 4px;

		&-edit {
			visibility: hidden;
			opacity: 0;
			transition: opacity 0.2s;
		}

		&:hover &-edit {
			visibility: visible;
			opacity: 1;
		}

		@media (hover: none) {
			&-edit {
				visibility: visible;
				opacity: 1;
			}
		}

		&-title,
		&-name {
			display: inline;
			font-size: 14px;
			line-height: 18px;
			color: #4D4D4D;
		}

		&-title {
			font-weight: 500;
		}

		&-name {
			margin-right: 4px;
			font-weight: 400;
		}
	}

	&__address {
		display: flex;
		gap: 4px;

		:deep(.inline-address-editor__actions--view) {
			visibility: hidden;
			opacity: 0;
			transition: opacity 0.2s;
		}

		&:hover :deep(.inline-address-editor__actions--view) {
			visibility: visible;
			opacity: 1;
		}

		@media (hover: none) {
			:deep(.inline-address-editor__actions--view) {
				visibility: visible;
				opacity: 1;
			}
		}

		&-title,
		&-name {
			display: inline;
			font-size: 14px;
			line-height: 18px;
			color: #4D4D4D;
		}

		&-title {
			font-weight: 500;
			line-height: 1.5;
		}

		&-name {
			margin-right: 4px;
			font-weight: 400;
		}

		&-actions {
			display: inline-flex;
			align-items: center;
			gap: 2px;
			margin-left: 8px;
			vertical-align: middle;
		}
	}

	&__divider {
		width: 100%;
		border-bottom: 1px solid #F2F2F2;
		margin: 8px 0;
	}

	&__direction {
		display: flex;
		flex-direction: column;
		width: 100%;
		height: fit-content;

		&-permition {
			display: flex;
			align-items: center;
			justify-items: center;
			flex-direction: column;

			&-icon {
				position: relative;
				display: flex;
				flex-direction: column;
			}

			&-label {
				color: var(--Text-color-text-secondary, #979797);
				text-align: center;
				font-size: 16px;
				font-style: normal;
				font-weight: 600;
				line-height: 20px;

			}
		}

		&-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			flex-wrap: wrap;

			&-left,
			&-right{
				display: flex;
				align-items: center;
			}

			&-icon {
				margin-top: -3px;
				margin-right: 4px;
			}

			&-right,
			&-title {
				font-size: 14px;
				line-height: 18px;
				color: #4D4D4D;
			}

			&-title {
				font-weight: 500;
				margin-right: 2px;
				white-space: nowrap;
			}
			
			&-right {
				margin-left: 19px;

				&-debt {
					color: #D05A4F;
				}
			}
		}

		&-body {
			display: flex;
			flex-direction: column;
			width: 100%;
			height: fit-content;
			margin-top: 8px;

			&--cp,
			&--uc,
			&--aw {
				gap: 2px;
			}

			&--cp {
				max-height: 150px;
				overflow-y: auto;
			}

			&--uc {
				max-height: 104px;
				overflow-y: auto;
			}
		}

		&-cp,
		&-uc,
		&-aw {
			display: flex;
			align-items: center;
			justify-content: space-between;

			&-left {
				display: flex;
				align-items: center;
				font-size: 14px;
				line-height: 18px;
				gap: 2px;

				&-count {
					margin: 0 2px 0 3px;
				}
				
				&-name,
				&-count,
				&-address {
					color: #4D4D4D;
				}

				&-address {
					white-space: nowrap;
				}

				&-name {
					&--removed {
						color: #979797;
					}
				}
			}

			&-right {
				color: #4D4D4D;
			}
		}

		&-cp {
			align-items: flex-start;

			&-left {
				align-items: flex-start;
			}

			&-right {
				margin-left: 8px;
			}
		}

		&-uc {
			&-left {
				align-items: flex-start;

				&-name {
					&-str {
						color: #438451;
						cursor: pointer;
						text-decoration: none;

						&:hover {
							text-decoration: underline;
						}
					}
				}
			}
		}

		&-aw {
			&-left {
				align-items: flex-start;

				&-roles {
					display: inline;
					margin-left: 2px;

					&-item {
						display: inline-flex;
						align-items: center;
						vertical-align: middle;
						margin-right: 2px;

						&-name {
							color: #979797;
							margin-right: 2px;
						}
					}
				}
			}
		}
	}
}

.client-page__direction-header-add-anchor {
	display: inline-flex;
	align-items: center;
	vertical-align: middle;
	cursor: pointer;
	line-height: 0;
}
</style>
